import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  IconButton,
  TextField,
} from "@mui/material";
import {
  Close as CloseIcon,
  Lock as LockIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { changePassword } from "../auth/auth.service";

const ChangePasswordDialog = ({ open, onClose }) => {
  const [success, setSuccess] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [currentPasswordError, setCurrentPasswordError] = useState("");
  const [newPasswordError, setNewPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");

  // Reset state when open dialog
  useEffect(() => {
    if (open) {
      setCurrentPasswordError("");
      setNewPasswordError("");
      setConfirmPasswordError("");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setSuccess(false);
    }
  }, [open]);

  const handleSaveChange = async () => {
    let error = false;

    if (!currentPassword || currentPassword.length < 6) {
      setCurrentPasswordError("Password must be at least 6 characters long.");
      error = true;
    }
    if (!newPassword || newPassword.length < 6) {
      setNewPasswordError("Password must be at least 6 characters long.");
      error = true;
    }
    if (!confirmPassword || confirmPassword.length < 6) {
      setConfirmPasswordError("Password must be at least 6 characters long.");
      error = true;
    }
    if (newPassword !== confirmPassword) {
      setConfirmPasswordError("Password doesn't match.");
      error = true;
    }
    if (error) return;

    // Call service
    try {
      await changePassword(currentPassword, newPassword);
      setSuccess(true);
    } catch (error) {
      setCurrentPasswordError("Password Incorrect");
    }
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setCurrentPassword(value);

    if (currentPasswordError !== "" && value) setCurrentPasswordError("");
  };

  const handleNewPasswordChange = (e) => {
    const value = e.target.value;
    setNewPassword(value);

    if (newPasswordError !== "" && value) setNewPasswordError("");
  };

  const handleConfirmPassword = (e) => {
    const value = e.target.value;
    setConfirmPassword(value);
    if (confirmPasswordError !== "" && value) setConfirmPasswordError("");
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{
        zIndex: 1400,
      }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <LockIcon color="primary" />
            <Typography variant="h6">Change Password</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {!success ? (
        <DialogContent dividers>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Current Password
            </Typography>
            <TextField
              fullWidth
              error={currentPasswordError !== ""}
              helperText={currentPasswordError}
              value={currentPassword}
              onChange={handlePasswordChange}
              placeholder="Enter your current password"
              type="password"
              variant="outlined"
              size="small"
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              New Password
            </Typography>
            <TextField
              fullWidth
              error={newPasswordError !== ""}
              helperText={newPasswordError}
              value={newPassword}
              onChange={handleNewPasswordChange}
              placeholder="Enter your new password"
              type="password"
              variant="outlined"
              size="small"
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Confirm New Password
            </Typography>
            <TextField
              fullWidth
              error={confirmPasswordError !== ""}
              helperText={confirmPasswordError}
              value={confirmPassword}
              onChange={handleConfirmPassword}
              placeholder="Confirm your new password"
              type="password"
              variant="outlined"
              size="small"
            />
          </Box>

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              Password must be at least 6 characters long.
            </Typography>
          </Alert>
        </DialogContent>
      ) : (
        <DialogContent dividers>
          <Box sx={{ 
            display: "flex", 
            flexDirection: "column", 
            alignItems: "center", 
            py: 4 
          }}>
            <CheckCircleIcon 
              color="success" 
              sx={{ fontSize: 48, mb: 2 }} 
            />
            <Typography variant="h6" color="success.main" gutterBottom>
              Password Updated Successfully
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              Your password has been changed successfully. You can now use your new password to sign in.
            </Typography>
          </Box>
        </DialogContent>
      )}

      <DialogActions>
        {!success ? (
          <>
            <Button onClick={onClose}>
              Cancel
            </Button>
            <Button 
              variant="contained" 
              onClick={handleSaveChange}
            >
              Save Changes
            </Button>
          </>
        ) : (
          <Button variant="contained" onClick={onClose}>
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ChangePasswordDialog;
